import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
import { ProjectChartRowsLoading } from '~/modules/resourcing/common/components';
import PageOutOfBoundsMessage from '~/modules/common/components/ListTable/PageOutOfBoundsMessage';
import { AllocationRowLoading } from '~/modules/resourcing/common/components/ProjectChartRowsLoading/ProjectChartRowsLoading';
import {
  useResourcePlanBulkUserActualSeriesByRoles,
  useResourceUserRoleScheduleAsOfDate,
  useScheduleDetailsForResourceUser
} from '~/modules/resourcing/common/hooks';
import { mapScaleToPeriodResolution } from '~/modules/resourcing/common/util';
import { useResourcingChartToolbarContext } from '~/modules/projects/resourcing-plan/hooks';
import { useMeContext } from '~/modules/me';
import { getTodayForUser } from '~/modules/common/dates/today';
import { useResourceAllocationChartFocusContext } from '../ResourcingPlanPage/contexts';
import { ResourceAllocationChartRow2 } from './components/ResourceAllocationChart/components';
import {
  useFetchResourcePlanUsersAndRoles,
  useOtherProjectAllocationSummaryForUsers,
  useUserRowExpansionPanelStateReducer
} from './hooks';
import NoDataChartRow from './components/NoDataChartRow';

const useStyles = makeStyles(() => ({
  container: {
    width: '100%'
  }
}));

export const ResourceAllocationChartContent2 = ({
  allocatedUsers,
  autofocusedUserId,
  currentPage,
  handleEditResourceAllocation,
  handleRemoveResourceAllocation,
  refetchResourcePlanUsers,
  isResourceActualModeEnabled,
  loadingUsers,
  projectId,
  resourceRequestAcceptedLoading,
  setSnackBarState,
  setAutofocusedUserId,
  setCurrentPage,
  updateAllocationLoadingUserUri
}) => {
  const classes = useStyles();

  const { setFocusToHeader } = useResourceAllocationChartFocusContext() || {};

  const {
    onUserRowExpansionPanelClick,
    userExpandState,
    resetExpansionPanelStateForAllUserRows
  } = useUserRowExpansionPanelStateReducer();

  const me = useMeContext();
  const {
    resourceCostMode,
    featureFlags: { isPsaRmpTaskAllocation1Enabled, isRmp2025Q4Enhancements },
    permissionsMap
  } = me;

  const hasViewProjectRolePermission = Boolean(
    permissionsMap['urn:replicon:user-action:view-project-role']
  );

  const today = getTodayForUser(me);

  const { chartDates, dateRange, scale } = useResourcingChartToolbarContext();

  const userIds = allocatedUsers.map(x => x.uri);

  const {
    allocatedUsersAndRoles,
    loading: isUsersAndRolesRowsLoading
  } = useFetchResourcePlanUsersAndRoles({
    projectId,
    userIds,
    loadingUsers,
    isResourceActualModeEnabled
  });

  const {
    loading: loadingUserScheduleDetails,
    scheduleDetailsByUserMap
  } = useScheduleDetailsForResourceUser({
    userIds,
    dateRange,
    periodResolution: mapScaleToPeriodResolution(scale),
    loadingUsers,
    includeResourceAvailabilitySummary: true
  });

  const {
    roleSchedulesAsOfDateByUserUri
  } = useResourceUserRoleScheduleAsOfDate({
    userIds,
    asOfDate: today,
    skip:
      !isRmp2025Q4Enhancements ||
      !hasViewProjectRolePermission ||
      loadingUsers ||
      !userIds.length
  });

  const {
    loading: loadingOtherProjectAllocationsSummary,
    otherProjectAllocationSummaryByPeriodMap
  } = useOtherProjectAllocationSummaryForUsers({
    userIds,
    dateRange,
    periodResolution: mapScaleToPeriodResolution(scale),
    projectId,
    skip:
      !isPsaRmpTaskAllocation1Enabled ||
      Boolean(!userIds?.length) ||
      loadingUsers
  });

  const {
    isLoading: isActualsLoading,
    bulkResourcePlanUserActualSeriesByRoleMap
  } = useResourcePlanBulkUserActualSeriesByRoles({
    dateRange,
    scale,
    projectId,
    userUris: userIds,
    skip: loadingUsers || !isResourceActualModeEnabled || userIds.length === 0
  });

  const resetResourceAllocationChart = useCallback(() => {
    refetchResourcePlanUsers();
    resetExpansionPanelStateForAllUserRows();

    if (setFocusToHeader) {
      setFocusToHeader();
    }
  }, [
    resetExpansionPanelStateForAllUserRows,
    refetchResourcePlanUsers,
    setFocusToHeader
  ]);

  return (
    <>
      {allocatedUsersAndRoles.map(allocatedUser => (
        <div className={classes.container} key={allocatedUser.user.uri}>
          {updateAllocationLoadingUserUri === allocatedUser.user.uri ? (
            <AllocationRowLoading />
          ) : (
            <ResourceAllocationChartRow2
              allocatedUser={allocatedUser}
              projectId={projectId}
              currentPage={currentPage}
              handleEditResourceAllocation={handleEditResourceAllocation}
              handleRemoveResourceAllocation={handleRemoveResourceAllocation}
              resetResourceAllocationChart={resetResourceAllocationChart}
              setSnackBarState={setSnackBarState}
              updateAllocationLoadingUserUri={updateAllocationLoadingUserUri}
              autofocusedUserId={autofocusedUserId}
              setAutofocusedUserId={setAutofocusedUserId}
              resourceCostMode={resourceCostMode}
              loadingUserScheduleDetails={loadingUserScheduleDetails}
              loadingOtherProjectAllocationsSummary={
                loadingOtherProjectAllocationsSummary
              }
              onUserRowExpansionPanelClick={onUserRowExpansionPanelClick}
              isUserRowExpanded={Boolean(
                userExpandState[allocatedUser.user.id]
              )}
              userScheduleDetails={
                // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
                scheduleDetailsByUserMap[allocatedUser.user.uri] || {}
              }
              roleScheduleAsOfDate={
                roleSchedulesAsOfDateByUserUri[allocatedUser.user.uri]
              }
              otherProjectAllocationsSummary={
                otherProjectAllocationSummaryByPeriodMap[
                  allocatedUser.user.uri
                  // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
                ] || {}
              }
              isActualsLoading={isActualsLoading}
              userActuals={
                bulkResourcePlanUserActualSeriesByRoleMap[
                  allocatedUser.user.uri
                ]?.actualSeriesByRole || [] // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop
              }
            />
          )}
        </div>
      ))}
      {resourceRequestAcceptedLoading ? <AllocationRowLoading /> : null}
      {loadingUsers || isUsersAndRolesRowsLoading ? (
        <ProjectChartRowsLoading />
      ) : null}
      {!loadingUsers &&
        !isUsersAndRolesRowsLoading &&
        !allocatedUsers.length &&
        currentPage > 1 && (
          <PageOutOfBoundsMessage
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      {!loadingUsers &&
        !isUsersAndRolesRowsLoading &&
        !allocatedUsers.length &&
        currentPage === 1 && (
          <NoDataChartRow
            scale={scale}
            scaleItemCount={chartDates.length}
            resourceKey="resourceAllocationChartContent.noRows"
          />
        )}
    </>
  );
};

ResourceAllocationChartContent2.propTypes = {
  allocatedUsers: PropTypes.array,
  loadingUsers: PropTypes.bool.isRequired,
  projectId: PropTypes.string,
  currentPage: PropTypes.number,
  handleRemoveResourceAllocation: PropTypes.func,
  handleEditResourceAllocation: PropTypes.func,
  setCurrentPage: PropTypes.func,
  updateAllocationLoadingUserUri: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool
  ]),
  resourceRequestAcceptedLoading: PropTypes.bool,
  isResourceActualModeEnabled: PropTypes.bool,
  setSnackBarState: PropTypes.func,
  autofocusedUserId: PropTypes.string,
  setAutofocusedUserId: PropTypes.func,
  refetchResourcePlanUsers: PropTypes.func.isRequired
};

export default ResourceAllocationChartContent2;
